import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './App.css';
import ThinkingIndicator from './components/ThinkingIndicator';
import SkeletonLoader from './components/SkeletonLoader';
import SuggestionPanel from './components/SuggestionPanel';
import { ReactComponent as SparklesIcon } from './assets/sparkles.svg';
import { ReactComponent as MinusIcon } from './assets/minus.svg';
import { ReactComponent as BookIcon } from './assets/book.svg';
import { ReactComponent as PeddleIcon } from "./assets/peddle.svg";
import { ReactComponent as ArrowIcon } from './assets/arrow.svg';
import { ReactComponent as TerminalIcon } from './assets/terminal.svg';
import { ReactComponent as CopyIcon } from './assets/copy.svg';
import { ReactComponent as TickIcon } from './assets/tick.svg';
import AvatarGif from './components/AvatarGif';
import { fetchPendoContextAndInitialize } from './services/pendoService';
import { useLicenseVerification } from './hooks/useLicenseVerification';

interface Message {
	text: string;
	sender: 'user' | 'assistant';
	cancelled?: boolean;
}

interface AppProps {
	apiUrl?: string;
	sessionId?: string;
	params?: {
		system_type?: string;
		system_url?: string;
		system_token?: string;
		[key: string]: any;
		firstName?: string;
	};
	onRemove?: () => void;
}

// Memoize table styles
const tableStyle = {
	borderCollapse: 'collapse' as const,
	width: '100%',
	marginBottom: '16px',
};

const theadStyle = {
	backgroundColor: '#f2f2f2',
};

const trStyle = {
	borderBottom: '1px solid #ddd',
};

const thStyle = {
	border: '1px solid #ddd',
	padding: '8px',
	textAlign: 'left' as const,
	fontWeight: 'bold',
};

const tdStyle = {
	border: '1px solid #ddd',
	padding: '8px',
};

// Memoize remarkPlugins array
const remarkPlugins = [remarkGfm];


const normalizeMarkdown = (text: string): string => {
	if (!text) return text;
	return text.replace(/\n---\n/g, '\n\n---\n');
};

const App = memo(function App({
	apiUrl = process.env.REACT_APP_API_URL,
	sessionId: providedSessionId,
	params = {},
	onRemove,
}: AppProps) {
	const [isMinimized, setIsMinimized] = useState(true);
	const [isMaximizing, setIsMaximizing] = useState(false);
	const [isMinimizing, setIsMinimizing] = useState(false);
	const [isInitialRender, setIsInitialRender] = useState(true);
	const [isChatFullyOpened, setIsChatFullyOpened] = useState(false);
	const [message, setMessage] = useState('');
	const [messages, setMessages] = useState<Message[]>([]);
	const [sessionId] = useState(providedSessionId || uuidv4());
	const [isLoading, setIsLoading] = useState(false);
	const [isInitializing, setIsInitializing] = useState(true);
	const [showSuggestions, setShowSuggestions] = useState(true);
	const [messageCount, setMessageCount] = useState<number | null>(null);
	const [position, setPosition] = useState<'left' | 'right'>('right');
	const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null); // Track cursor position during drag

	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const isDraggingRef = useRef(false);
	const dragStartRef = useRef({ x: 0, y: 0 });
	const justFinishedDraggingRef = useRef(false);
	const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);
 	const chatWindowRef = useRef<HTMLDivElement>(null);
 	const inputRef = useRef<HTMLTextAreaElement>(null);

	// Memoize isAgentUnavailable calculation
	const isAgentUnavailable = useMemo(() => 
		!params.system_type,
		[params.system_type]
	);

	const { hasLicenseAccess } = useLicenseVerification({
		apiUrl,
		systemUrl: params?.system_url,
		systemToken: params?.system_token,
		systemType: params?.system_type,
	});

	useEffect(() => {
		setIsMinimized(true);
		const raf = requestAnimationFrame(() => {
			setIsInitialRender(false);
		});

		return () => cancelAnimationFrame(raf);
	}, []);

	useEffect(() => {
		const initSession = async () => {
			if (sessionId && apiUrl && params) {
				try {
					const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
					const response = await fetch(`${apiUrl}/initSession`, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							session_id: sessionId,
							parameters: {
								...params,
								user_timezone: userTimezone,
							},
						}),
					});

					if (response.ok) {
						const data = await response.json();
						if (data.metadata && data.metadata.message_count !== undefined) {
							setMessageCount(data.metadata.message_count);
						}
					}
				} catch (error) {
					console.error('Error initializing session:', error);
				}
			}
		};
		initSession();
	}, [sessionId, apiUrl, params]);

	useEffect(() => {
		const loadPreviousMessages = async () => {
			if (sessionId && !isAgentUnavailable) {
				try {
					setIsInitializing(true);
					setIsLoading(true);
					const response = await fetch(
						`${apiUrl}/chat/${sessionId}/messages`
					);

					if (response.ok) {
						const previousMessages = await response.json();
						if (previousMessages.length > 0) {
							// Convert messages from backend format to frontend format
							const formattedMessages = previousMessages.map(
								(msg: any) => ({
									text: msg.content,
									sender:
										msg.role === 'assistant' ||
										msg.role === 'planner'
											? 'assistant'
											: 'user',
								})
							);
							setMessages(formattedMessages);
							setShowSuggestions(false);

							// If there are previous messages, open the chat automatically
							setIsMinimized(false);
							setIsMaximizing(true);
							setIsChatFullyOpened(false);

							// Use setTimeout to ensure the chat is fully rendered before scrolling
							setTimeout(() => {
								setIsMaximizing(false);
								setIsChatFullyOpened(true);

								// Ensure scrolling to the bottom after chat is opened
								if (chatWindowRef.current) {
									const { scrollHeight } = chatWindowRef.current;
									chatWindowRef.current.scrollTo({
										top: scrollHeight,
										behavior: 'smooth',
									});
								}
							}, 500);
						}
					}
				} catch (error) {
					console.error('Error loading previous messages:', error);
				} finally {
					setIsInitializing(false);
					setIsLoading(false);
				}
			} else {
				setIsInitializing(false);
			}
		};

		loadPreviousMessages();
	}, [sessionId, apiUrl, isAgentUnavailable]);

	useEffect(() => {
		if (chatWindowRef.current && messages.length > 0) {
			setTimeout(() => {
				if (chatWindowRef.current) {
					const { scrollHeight } = chatWindowRef.current;
					chatWindowRef.current.scrollTo({
						top: scrollHeight,
						behavior: 'smooth',
					});
				}
			}, 0);
		}
	}, [messages]);

	useEffect(() => {
		if (isChatFullyOpened && chatWindowRef.current && messages.length > 0) {
			setTimeout(() => {
				if (chatWindowRef.current) {
					const { scrollHeight } = chatWindowRef.current;
					chatWindowRef.current.scrollTo({
						top: scrollHeight,
						behavior: 'smooth',
					});
				}
			}, 100);
		}
	}, [isChatFullyOpened, messages.length]);

	useEffect(() => {
		if (!isMinimized && !isInitializing && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isMinimized, isInitializing]);

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.style.height = 'auto';
			inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
		}
	}, [message]);

	useEffect(() => {
		if (sessionId) {
			fetchPendoContextAndInitialize(sessionId, apiUrl)
				.then(success => {
					if (success) {
						console.log('Pendo initialized successfully');
					}
				})
				.catch(error => {
					console.warn('Error initializing Pendo:', error);
				});
		}
	}, [sessionId, apiUrl]);

	const sendMessageToBackend = useCallback(async (text: string): Promise<{response: string, cancelled?: boolean}> => {
		try {
			const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

			const response = await fetch(`${apiUrl}/chat`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					message: text,
					session_id: sessionId,
					parameters: {
						...params,
						user_timezone: userTimezone,
					},
				}),
			});

			if (!response.ok) {
				throw new Error('Network response was not ok');
			}

			const data = await response.json();
			
			// Update message count if provided
			if (data.metadata && data.metadata.message_count !== undefined) {
				setMessageCount(data.metadata.message_count);
			}
			
			return {
				response: data.response,
				cancelled: data.metadata?.cancelled || false
			};
		} catch (error) {
			console.error('Error:', error);
			return {
				response: 'Sorry, I encountered an error. Please try again.',
				cancelled: false
			};
		}
	}, [apiUrl, sessionId, params]);

	const handleSubmit = useCallback(async (e: React.FormEvent, customMessage?: string) => {
		e.preventDefault();
		const messageToSubmit = customMessage || message;
		if (!messageToSubmit.trim()) return;

		const userMessage = messageToSubmit;
		setMessage('');
		setMessages((prev) => [...prev, { text: userMessage, sender: 'user' }]);
		setIsLoading(true);
		setShowSuggestions(false);

		try {
			const result = await sendMessageToBackend(userMessage);
			setMessages((prev) => [
				...prev,
				{ text: result.response, sender: 'assistant', cancelled: result.cancelled },
			]);
		} catch (error) {
			console.error('Error:', error);
			setMessages((prev) => [
				...prev,
				{
					text: 'Sorry, I encountered an error. Please try again.',
					sender: 'assistant',
				},
			]);
		} finally {
			setIsLoading(false);
		}
	}, [message, sendMessageToBackend]);

	const handleSuggestionClick = useCallback((suggestion: string) => {
		setMessage(suggestion);
		const syntheticEvent = { preventDefault: () => {} } as React.FormEvent;
		handleSubmit(syntheticEvent, suggestion);
	}, [handleSubmit]);

	const handleSuggestionInsert = useCallback((suggestion: string) => {
		setMessage(suggestion);
	}, []);

	const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	}, [handleSubmit]);

	const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		e.stopPropagation();
	}, []);

	const clearSession = useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await fetch(`${apiUrl}/chat/${sessionId}`, {
				method: 'DELETE',
			});

			if (response.ok) {
				setMessages([]);
				setShowSuggestions(true);

				if (onRemove) {
					onRemove();
				}
			} else {
				console.error('Failed to clear session');
			}
		} catch (error) {
			console.error('Error clearing session:', error);
		} finally {
			setIsLoading(false);
		}
	}, [apiUrl, sessionId, onRemove]);

	const cancelChatRequest = useCallback(async () => {
		try {
			const response = await fetch(`${apiUrl}/chat/${sessionId}/cancel`, {
				method: 'DELETE',
			});

			if (response.ok) {
				console.log('Chat request cancelled');
			} else {
				console.error('Failed to cancel chat request');
			}
		} catch (error) {
			console.error('Error cancelling chat request:', error);
		}
	}, [apiUrl, sessionId]);

	const handleMaximize = useCallback(() => {
		setIsMaximizing(true);
		setIsMinimized(false);
		setIsChatFullyOpened(false);
		setTimeout(() => {
			setIsMaximizing(false);
			setIsChatFullyOpened(true);
			if (chatWindowRef.current) {
				const { scrollHeight } = chatWindowRef.current;
				chatWindowRef.current.scrollTo({
					top: scrollHeight,
					behavior: 'smooth',
				});
			}
		}, 700); // Increased to match the animation duration
	}, []);

	const handleToggleMinimize = useCallback(() => {
		if (!isMinimized) {
			setIsMinimizing(true);
			setIsChatFullyOpened(false);
			setTimeout(() => {
				setIsMinimized(true);
				setIsMinimizing(false);
			}, 600); // Increased to match the animation duration
		} else {
			setIsMaximizing(true);
			setIsMinimized(false);
			setIsChatFullyOpened(false);
			setTimeout(() => {
				setIsMaximizing(false);
				setIsChatFullyOpened(true);
				if (chatWindowRef.current) {
					const { scrollHeight } = chatWindowRef.current;
					chatWindowRef.current.scrollTo({
						top: scrollHeight,
						behavior: 'smooth',
					});
				}
			}, 700); // Increased to match the animation duration
		}
	}, [isMinimized]);

	const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setMessage(e.target.value);

		// Auto-resize the textarea
		if (e.target) {
			e.target.style.height = 'auto';
			e.target.style.height = `${e.target.scrollHeight}px`;
		}
	}, []);

	const handleCopyMessage = useCallback((text: string, index: number) => {
		navigator.clipboard.writeText(text);
		setCopiedMessageIndex(index);
		setTimeout(() => {
			setCopiedMessageIndex(null);
		}, 2000);
	}, []);

	const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
		if (!isMinimized) return;

		e.stopPropagation(); // Stop event from bubbling to parent app

		const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
		const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
		dragStartRef.current = { x: clientX, y: clientY };
		setDragStart({ x: clientX, y: clientY });

	}, [isMinimized]);

	const handleDragMove = useCallback((e: MouseEvent | TouchEvent) => {
		if (dragStartRef.current.x === 0 && dragStartRef.current.y === 0) return;

		const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
		const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

		// Calculate distance moved
		const deltaX = clientX - dragStartRef.current.x;
		const deltaY = clientY - dragStartRef.current.y;
		const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

		// Only start dragging if moved more than 3px (to distinguish from click)
		// Lower threshold makes it more responsive to fast movements
		if (distance > 3 && !isDraggingRef.current) {
			isDraggingRef.current = true;
			setIsDragging(true);
			e.preventDefault();
			e.stopPropagation();
		}

		if (isDraggingRef.current) {
			e.preventDefault();
			e.stopPropagation();

			// Update drag position to follow cursor in both X and Y
			// Avatar is 40px, so offset by 20px to center on cursor
			// Ensure it doesn't go off-screen (min 20px from edges to match snap position)
			const avatarSize = 40;
			const edgeMargin = 20; // Match the snap position margin

			// Calculate X position with bounds
			let dragX = clientX - (avatarSize / 2);
			dragX = Math.max(edgeMargin, Math.min(dragX, window.innerWidth - avatarSize - edgeMargin));

			// Calculate Y position with bounds (from bottom of window)
			// clientY is from top, so convert to bottom-based coordinate
			let dragY = window.innerHeight - clientY - (avatarSize / 2);
			dragY = Math.max(edgeMargin, Math.min(dragY, window.innerHeight - avatarSize - edgeMargin));

			setDragPosition({ x: dragX, y: dragY });

			// Determine if we're closer to left or right (for final snap)
			const windowWidth = window.innerWidth;
			const newPosition = clientX < windowWidth / 2 ? 'left' : 'right';
			setPosition(newPosition);
		}
	}, []);

	const handleDragEnd = useCallback(() => {
		if (isDraggingRef.current) {
			isDraggingRef.current = false;
			setIsDragging(false);
			setDragPosition(null); // Clear drag position to snap to final location
			justFinishedDraggingRef.current = true; // Prevent click from opening chat

			// Clear the flag after a short delay to allow normal clicks
			setTimeout(() => {
				justFinishedDraggingRef.current = false;
			}, 100);
		}
		dragStartRef.current = { x: 0, y: 0 };
		setDragStart({ x: 0, y: 0 });
	}, [position]);

	const handleMoveGlobal = useCallback((e: MouseEvent | TouchEvent) => {
		handleDragMove(e);
	}, [handleDragMove]);

	const handleEndGlobal = useCallback((e: MouseEvent | TouchEvent) => {
		handleDragEnd();
	}, [handleDragEnd]);

	useEffect(() => {
		// Listen to drag events when we have a drag start position
		if (dragStart.x !== 0 || dragStart.y !== 0) {

			// Add class to body to prevent interactions
			document.body.classList.add('xaia-dragging');
			document.body.style.cursor = 'grabbing';
			document.body.style.userSelect = 'none';

			// Attach to body element directly
			document.body.addEventListener('mousemove', handleMoveGlobal as any, true);
			document.body.addEventListener('mouseup', handleEndGlobal as any, true);
			document.addEventListener('mousemove', handleMoveGlobal as any, true);
			document.addEventListener('mouseup', handleEndGlobal as any, true);

			return () => {
				document.body.classList.remove('xaia-dragging');
				document.body.style.cursor = '';
				document.body.style.userSelect = '';

				document.body.removeEventListener('mousemove', handleMoveGlobal as any, true);
				document.body.removeEventListener('mouseup', handleEndGlobal as any, true);
				document.removeEventListener('mousemove', handleMoveGlobal as any, true);
				document.removeEventListener('mouseup', handleEndGlobal as any, true);
			};
		}
	}, [dragStart, handleMoveGlobal, handleEndGlobal]);

	const chatWidgetClassName = useMemo(() => {
		let className = 'chat-widget';
		if (isAgentUnavailable) {
			className += ' agent-unavailable';
		}

		if (isMinimized) {
			className += ' minimized';
		} else if (isMinimizing) {
			className += ' minimizing';
		} else if (isMaximizing) {
			className += ' maximizing';
		}
		if (isInitialRender) {
			className += ' initial-render';
		}

		if (isChatFullyOpened) {
			className += ' fully-opened';
		}

		className += ` position-${position}`;

		if (isDragging) {
			className += ' dragging';
		}

		return className;
	}, [isAgentUnavailable, isMinimized, isMaximizing, isMinimizing, isInitialRender, isChatFullyOpened, position, isDragging]);

	const isSendButtonDisabled = useMemo(() => {
		return isLoading || !message.trim();
	}, [isLoading, message]);

	const getHelpDocumentationUrl = useCallback((systemType?: string) => {
		return systemType === 'xtrf'
			? "https://xtm-cloud.atlassian.net/wiki/spaces/XKB/pages/4412342286/Use+the+XTRF+AI+Copilot+xaia"
			: "https://help.xtm.cloud/en/xtm-cloud/25.6/en/xtm-agent.html";
	}, []);

	const markdownComponents = useMemo(() => ({
		table: ({ node, ...props }: any) => (
			<table
				style={tableStyle}
				{...props}
			/>
		),
		thead: ({ node, ...props }: any) => (
			<thead
				style={theadStyle}
				{...props}
			/>
		),
		tbody: ({ node, ...props }: any) => (
			<tbody {...props} />
		),
		tr: ({ node, ...props }: any) => (
			<tr
				style={trStyle}
				{...props}
			/>
		),
		th: ({ node, ...props }: any) => (
			<th
				style={thStyle}
				{...props}
			/>
		),
		td: ({ node, ...props }: any) => (
			<td
				style={tdStyle}
				{...props}
			/>
		),

		a: ({ node, ...props }: any) => {
			const href = props.href || '';
			const shouldOpenInNewTab = href.startsWith('https://xtm-cloud.atlassian.net') || 
									   href.startsWith('https://help.xtm.cloud');
			
			return (
				<a
					{...props}
					target={shouldOpenInNewTab ? "_blank" : undefined}
					rel={shouldOpenInNewTab ? "noopener noreferrer" : undefined}
				/>
			);
		},
	}), []);

	const handleAvatarClick = useCallback((e: React.MouseEvent) => {
		// Don't open chat if we just finished dragging
		if (justFinishedDraggingRef.current) {
			e.preventDefault();
			e.stopPropagation();
			return;
		}
		handleMaximize();
	}, [handleMaximize]);

	const wrapperClassName = useMemo(() => {
		return `xtm-assistant-wrapper position-${position}`;
	}, [position]);

	return (
		<div className={wrapperClassName}>
			{isDragging && (
				<div
					className="xaia-drag-overlay"
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						width: '100vw',
						height: '100vh',
						zIndex: 10001,
						cursor: 'grabbing',
						backgroundColor: 'transparent',
						pointerEvents: 'auto',
					}}
					onMouseMove={(e) => {
						handleDragMove(e.nativeEvent);
					}}
					onMouseUp={(e) => {
						handleDragEnd();
					}}
					onTouchMove={(e) => {
						handleDragMove(e.nativeEvent);
					}}
					onTouchEnd={(e) => {
						handleDragEnd();
					}}
					data-testid="drag-overlay"
				/>
			)}
			{isMinimized && !isInitialRender && (
				<div
					className={`minimized-avatar-container position-${position} ${isDragging ? 'dragging' : ''}`}
					style={{
						position: 'fixed',
						// During drag: follow cursor in both X and Y
						// After drag: snap to bottom-left (20px, 20px) or bottom-right (calc, 20px)
						bottom: dragPosition ? `${dragPosition.y}px` : '20px',
						left: dragPosition
							? `${dragPosition.x}px`
							: (position === 'left' ? '20px' : `calc(100vw - 60px)`),
						right: 'auto', // Always auto to avoid property switching
						zIndex: 10002, // Above overlay
						transition: isDragging ? 'none' : 'left 0.3s ease, bottom 0.3s ease',
						pointerEvents: isDragging ? 'none' : 'auto', // Let overlay handle events during drag
					}}
					data-position={position}
					data-is-dragging={isDragging}
					data-testid="minimized-avatar-container"
				>
					<AvatarGif
						key="stable-gif-key"
						className="avatar-icon standalone"
						height={40}
						width={40}
						onClick={handleAvatarClick}
						onMouseDown={handleDragStart}
						onTouchStart={handleDragStart}
						showCloseButton={true}
						data-testid="minimized-avatar"
					/>
				</div>
			)}
			<div
				className={chatWidgetClassName}
				data-testid="chat-widget"
			>
				<AvatarGif
					key="stable-gif-key"
					className="avatar-icon"
					height={40}
					width={40}
					onClick={handleMaximize}
					data-testid="header-avatar"
				/>
				<div
					className="chat-header"
					data-testid="chat-header"
				>
					{isInitializing ? (
						<span className="title" data-testid="title-skeleton">
							<SkeletonLoader type="text" width={40} height={20} data-testid="title-text-skeleton" />
							<span className="ai-indicator" data-testid="ai-indicator-skeleton">
									<SkeletonLoader type="text" width={20} height={16} data-testid="ai-text-skeleton" />
									<SkeletonLoader type="circle" width={20} height={16} style={{ marginLeft: '3px' }} data-testid="ai-circle-skeleton" />
							</span>
						</span>
					) : (
						<span className="title" data-testid="title">
							XTM Agent
							<span className="ai-indicator" data-testid="ai-indicator">
								<span className="ai-badge" data-testid="ai-badge">
									AI{' '}
									<SparklesIcon data-testid="sparkles-icon" />
								</span>
							</span>
						</span>
					)}
					<div className="header-controls" data-testid="header-controls">
						{messages.length > 0 && (
								<button
									onClick={clearSession}
									disabled={isLoading}
									title="Clear conversation"
									className="toggle-button no-hover"
									data-testid="clear-chat-button"
								>
									<PeddleIcon data-testid="peddle-icon" />
								</button>
						)}
							<button title="Help documentation" className="header-button no-hover" data-testid="book-button">
								<a 
									href={getHelpDocumentationUrl(params.system_type)}
									target="_blank" 
									rel="noreferrer"
								>
									<BookIcon data-testid="book-icon" />
								</a>
							</button>
						<button
							onClick={handleToggleMinimize}
							className="toggle-button no-hover"
							data-testid="minimize-button"
							title="Minimize"
						>
							<MinusIcon data-testid="minus-icon" />
						</button>
					</div>
				</div>
				<div
					className="chat-content"
					ref={chatWindowRef}
					data-testid="chat-content"
				>
					{showSuggestions &&
					!isAgentUnavailable &&
					messages.length === 0 ? (
						<SuggestionPanel
							onSuggestionClick={handleSuggestionClick}
							onSuggestionInsert={handleSuggestionInsert}
							systemType={params.system_type}
							isLoading={isLoading}
							firstName={params.firstName}
							hasLicenseAccess={hasLicenseAccess ?? false}
							data-testid="suggestion-panel"
						/>
					) : (
						messages.map((msg, index) => (
							<div
								key={index}
								className={`message ${msg.sender} ${msg.cancelled ? 'cancelled' : ''}`}
								data-testid={`message-${msg.sender}-${index}`}
							>
								{msg.cancelled ? (
									<div className="cancelled-message-content">
										<div className="cancelled-message-small">Response stopped.</div>
										<div className="cancelled-message-bold">How else can I help you?</div>
									</div>
								) : (
									<>
										<ReactMarkdown
											remarkPlugins={remarkPlugins}
											components={markdownComponents}
											data-testid={`markdown-${index}`}
										>
											{normalizeMarkdown(msg.text)}
										</ReactMarkdown>
										{msg.sender === 'assistant' && (
											<div className={`copy-message-container ${copiedMessageIndex === index ? 'copied' : ''}`}>
												<button
													className="copy-message-button"
													onClick={() => handleCopyMessage(msg.text, index)}
													data-testid={`copy-message-button-${index}`}
												>
													{copiedMessageIndex === index ? (
														<TickIcon data-testid={`tick-icon-${index}`} />
													) : (
														<CopyIcon data-testid={`copy-icon-${index}`} />
													)}
												</button>
												<span className="copy-tooltip">
													{copiedMessageIndex === index ? 'Copied!' : 'Copy'}
												</span>
											</div>
										)}
									</>
								)}
							</div>
						))
					)}
					{isInitializing && (
						<>
							<SkeletonLoader type="message" count={1} data-testid="message-skeleton" />
						</>
					)}
					{isLoading && !isInitializing && (
						<>
							<ThinkingIndicator />
						</>
					)}
					{isAgentUnavailable && (
						<div className="message system" data-testid="agent-unavailable-message">
							<p data-testid="agent-unavailable-text">AI Agent is not available</p>
						</div>
					)}
				</div>
				{!isAgentUnavailable && (
					<form
						onSubmit={handleSubmit}
						className="chat-input-container"
						data-testid="chat-input-form"
					>
						{isInitializing ? (
							<>
								<SkeletonLoader type="input-wrapper" width="100%" height={43} data-testid="input-skeleton" />
							</>
						) : (
							<>
								<div className="input-wrapper" data-testid="input-wrapper">
									<textarea
										value={message}
										onChange={handleInputChange}
										onKeyPress={handleKeyPress}
										onKeyDown={handleKeyDown}
										placeholder="Ask me..."
										className="message-input"
										data-testid="message-input"
										ref={inputRef as React.RefObject<HTMLTextAreaElement>}
										rows={1}
										disabled={isLoading}
									/>
									{isLoading ? (
										<button
											type="button"
											onClick={cancelChatRequest}
											className="cancel-button"
											title="Stop generating"
											data-testid="cancel-button"
										>
											<span className="stop-icon">⏹</span>
										</button>
									) : (
										<button
											type="submit"
											className={`send-button ${message.trim() ? 'has-text' : ''}`}
											disabled={isSendButtonDisabled}
											title="Send"
											data-testid="send-button"
										>
											<ArrowIcon data-testid="arrow-icon" />
										</button>
									)}
								</div>
							</>
						)}
					</form>
				)}
				<div className="policy-note" data-testid="policy-note">
					{isInitializing ? (
						<>
							<span data-testid="policy-skeleton-1">
								<SkeletonLoader type="text" width={230} height={10} data-testid="policy-text-skeleton-1" />
							</span>
							<span data-testid="policy-skeleton-2">
								<SkeletonLoader type="text" width={280} height={10} data-testid="policy-text-skeleton-2" />
							</span>
							<span data-testid="policy-skeleton-3">
								<SkeletonLoader type="text" width={120} height={10} data-testid="policy-text-skeleton-3" />
							</span>
						</>
					) : (
						<>
							<span data-testid="policy-text-3">
								Review our{' '}
								<a
									href="https://xtm.cloud/security-trust-center/"
									className="policy-link"
									data-testid="policy-link"
									target="_blank"
								>
									AI Policy.
								</a>
							</span>
						</>
					)}
				</div>
				{messageCount !== null && (
					<div 
						className="counter" 
						data-testid="message-counter"
						data-tooltip={
							messageCount > 50000 
								? "More than 50,000 messages were sent by all users in your instance. No worries, you can still chat with XTM Agent!"
								: `You've sent ${messageCount} messages this month. Message count is shared across all users on your instance.\nGuideline for fair usage: 50,000 messages/month`
						}
					>
						<TerminalIcon data-testid="terminal-icon" />
						<span>{messageCount > 50000 ? '>50,000' : messageCount}</span>
					</div>
				)}
			</div>
		</div>
	);
});

export default App;
